import { onScopeDispose, shallowRef } from 'vue'
import type { TCareerForm, TCareerLayout, TCareerSection, TCareerTheme, TCareerThemeModeKey } from '@bc-library/recruitment'
import type { IJobOffer } from '@/services/jobOffer/IJobOffers'

/**
 * Live preview of the job board customization page: the career site runs in a frame of the page, which
 * sends it the settings being edited (saved or not) with postMessage, same origin only. The offer form
 * previews its offer the same way. The frame is named
 * CAREER_PREVIEW_FRAME_NAME: a window keeps its name across navigations, so the offer pages opened from the
 * preview are previewed too.
 */
export const CAREER_PREVIEW_FRAME_NAME = 'bc-career-preview'
/** Offer page of the preview of an offer not created yet: nothing saved to load */
export const CAREER_PREVIEW_NEW_OFFER = 'preview'
const MESSAGE = 'bc-career-preview'
const READY_MESSAGE = 'bc-career-preview:ready'

/** Settings of the firm the preview shows instead of the saved ones */
export type TCareerPreviewFirm = {
  jobBoardTitle?: { FR?: string, EN?: string }
  jobBoardContent?: { FR?: string, EN?: string }
  jobBoardBanner?: string | null
  jobBoardBannerDark?: string | null
  jobBoardTheme?: TCareerTheme | null
  jobBoardLayout?: TCareerLayout
  jobBoardSections?: TCareerSection[]
  options?: { jobBoard?: { showFirstOfficeFilter?: boolean } }
  jobBoardForm?: TCareerForm
}

/** The offer being edited in the offer form, saved or not: its page shows it instead of the saved one */
export type TCareerPreviewOffer = Partial<Omit<IJobOffer, 'offices' | 'jobs' | 'teams' | 'endDate'>> & {
  offices?: { _id: string, display: string }[]
  jobs?: { _id: string, display: string }[]
  teams?: { _id: string, display: string }[]
  endDate?: string | null
}

export type TCareerPreviewState = {
  firm: TCareerPreviewFirm
  offer?: TCareerPreviewOffer | null
  /** Visitor mode shown by the preview, whatever the mode of the app */
  mode: TCareerThemeModeKey
  /** Language of the texts shown by the preview (the one being edited) */
  language: 'fr' | 'en'
}

/** Whether the app runs in the preview frame of the customization page (no app shell there) */
export const isCareerPreviewFrame = () => window.parent !== window && window.name === CAREER_PREVIEW_FRAME_NAME

/** Career pages: the settings sent by the customization page when they run in its preview, null otherwise */
export const useCareerPreviewState = () => {
  const state = shallowRef<TCareerPreviewState | null>(null)
  const isPreview = isCareerPreviewFrame()
  if (!isPreview)
    return { isPreview, state }

  const onMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin || event.source !== window.parent || event.data?.type !== MESSAGE)
      return
    state.value = event.data.state
  }
  window.addEventListener('message', onMessage)
  onScopeDispose(() => window.removeEventListener('message', onMessage))
  // The page sends its settings again when a page of the frame is ready to read them
  window.parent.postMessage({ type: READY_MESSAGE }, window.location.origin)
  return { isPreview, state }
}

/** Customization page: sends the settings to the preview frame */
export const sendCareerPreview = (frame: Window | null | undefined, state: TCareerPreviewState) => {
  frame?.postMessage({ type: MESSAGE, state: JSON.parse(JSON.stringify(state)) }, window.location.origin)
}

/** Customization page: `callback` runs each time a page of the preview frame asks for the settings */
export const onCareerPreviewReady = (getFrame: () => Window | null | undefined, callback: () => void) => {
  const onMessage = (event: MessageEvent) => {
    if (event.origin === window.location.origin && event.source === getFrame() && event.data?.type === READY_MESSAGE)
      callback()
  }
  window.addEventListener('message', onMessage)
  onScopeDispose(() => window.removeEventListener('message', onMessage))
}
